<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, ref, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import NotificationsAPI, {
  consentChannelFor,
  evaluateContactNotificationPreference,
  notificationPreferenceErrorState,
  normalizeNotificationChannel,
} from 'dashboard/api/notifications';

const props = defineProps({
  contact: { type: Object, default: () => ({}) },
  contactId: { type: [String, Number], default: null },
  initialPurpose: { type: String, default: 'MARKETING' },
});

const emit = defineEmits(['dispatchGuardChange']);

const CHANNELS = [
  { id: 'whatsapp', label: 'WhatsApp', icon: 'i-lucide-message-circle' },
  { id: 'email', label: 'e-mail', icon: 'i-lucide-mail' },
  { id: 'sms', label: 'SMS', icon: 'i-lucide-smartphone' },
];

const PURPOSES = [
  {
    id: 'MARKETING',
    label: 'Marketing e novidades',
    template: 'Modelo: campanhas e relacionamento',
  },
  {
    id: 'VISIT_REMINDER',
    label: 'Lembretes de visita',
    template: 'Modelo: confirmação e remarcação',
  },
  {
    id: 'NOTIMANAGER',
    label: 'Alertas de imóveis',
    template: 'Modelo: imóveis recomendados',
  },
];

const PROFILES = [
  { id: 'TENANT', label: 'Locatário/comprador' },
  { id: 'OWNER', label: 'Proprietário' },
  { id: 'AFFILIATE', label: 'Parceiro' },
];

const SOURCE = 'lead_profile_notification_preferences';

const snapshot = ref({ consents: [], defaults: {}, alerts: [] });
const preferenceLoadState = ref('idle');
const preferenceError = ref(null);
const selectedProfile = ref('TENANT');
const updateReason = ref('');
const savingKey = ref(null);

const normalizeStatus = value =>
  String(value || 'PENDING')
    .trim()
    .toUpperCase();

const normalizePurposeSelection = value =>
  PURPOSES.some(purpose => purpose.id === normalizeStatus(value))
    ? normalizeStatus(value)
    : 'MARKETING';

const selectedPurpose = ref(normalizePurposeSelection(props.initialPurpose));
const resolvedContactId = computed(() => props.contactId || props.contact?.id);
const isContactBlocked = computed(() =>
  Boolean(props.contact?.blocked || props.contact?.blocked_contact)
);
const canEdit = computed(() => preferenceError.value?.canEdit !== false);
const isLoading = computed(() => preferenceLoadState.value === 'loading');
const hasLoadError = computed(() =>
  ['error', 'forbidden', 'not_found'].includes(preferenceLoadState.value)
);

const consentFor = (purpose, channel) => {
  const purposeKey = normalizeStatus(purpose);
  const channelKey = consentChannelFor(channel);
  const profileKey = normalizeStatus(selectedProfile.value);

  return (snapshot.value.consents || []).find(
    consent =>
      normalizeStatus(consent.purpose) === purposeKey &&
      consentChannelFor(consent.channel) === channelKey &&
      normalizeStatus(consent.profile) === profileKey
  );
};

const statusMeta = consent => {
  if (!consent) {
    return {
      label: 'Sem registro',
      className: 'bg-slate-100 text-slate-700',
    };
  }

  const status = normalizeStatus(consent.effective_status || consent.status);
  return (
    {
      ACTIVE: {
        label: 'Opt-in',
        className: 'bg-emerald-50 text-emerald-700',
      },
      WITHDRAWN: {
        label: 'Opt-out',
        className: 'bg-rose-50 text-rose-700',
      },
      PENDING: {
        label: 'Pendente',
        className: 'bg-amber-50 text-amber-700',
      },
    }[status] || {
      label: status,
      className: 'bg-slate-100 text-slate-700',
    }
  );
};

const formatDateTime = value => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString('pt-BR');
};

const auditLine = consent => {
  const audit = consent?.audit || {};
  const actor = audit.actor?.name ? `Ator: ${audit.actor.name}` : '';
  const reason = audit.reason ? `Motivo: ${audit.reason}` : '';
  const source = audit.source ? `Origem: ${audit.source}` : '';
  const decidedAt = formatDateTime(audit.decided_at);
  const timestamp = decidedAt ? `Atualizado em ${decidedAt}` : '';
  return [source, reason, actor, timestamp].filter(Boolean).join(' · ');
};

const purposeRows = computed(() =>
  PURPOSES.map(purpose => ({
    ...purpose,
    channels: CHANNELS.map(channel => {
      const consent = consentFor(purpose.id, channel.id);
      const compatibility = evaluateContactNotificationPreference(
        snapshot.value,
        {
          channel: channel.id,
          purpose: purpose.id,
          profile: selectedProfile.value,
          contact: props.contact,
          isBlocked: isContactBlocked.value,
        }
      );

      return {
        ...channel,
        consent,
        compatibility,
        status: statusMeta(consent),
        audit: auditLine(consent),
      };
    }),
  }))
);

const selectedPurposeChecks = computed(() =>
  CHANNELS.map(channel => ({
    ...channel,
    ...evaluateContactNotificationPreference(snapshot.value, {
      channel: channel.id,
      purpose: selectedPurpose.value,
      profile: selectedProfile.value,
      contact: props.contact,
      isBlocked: isContactBlocked.value,
    }),
  }))
);

const incompatibleDispatches = computed(() =>
  preferenceLoadState.value === 'ready'
    ? selectedPurposeChecks.value.filter(check => !check.allowed)
    : []
);

const emitDispatchGuard = () => {
  emit('dispatchGuardChange', {
    purpose: selectedPurpose.value,
    ready: preferenceLoadState.value === 'ready',
    checks: selectedPurposeChecks.value,
  });
};

const loadPreferences = async () => {
  if (!resolvedContactId.value) {
    snapshot.value = { consents: [], defaults: {}, alerts: [] };
    preferenceLoadState.value = 'idle';
    emitDispatchGuard();
    return;
  }

  preferenceLoadState.value = 'loading';
  preferenceError.value = null;
  try {
    const { data } = await NotificationsAPI.getContactNotificationPreferences(
      resolvedContactId.value
    );
    snapshot.value = data || { consents: [], defaults: {}, alerts: [] };
    preferenceLoadState.value = 'ready';
  } catch (error) {
    const errorState = notificationPreferenceErrorState(error);
    preferenceError.value = errorState;
    preferenceLoadState.value = errorState.kind;
    snapshot.value = { consents: [], defaults: {}, alerts: [] };
    if (errorState.kind !== 'forbidden') useAlert(errorState.message);
  } finally {
    emitDispatchGuard();
  }
};

const consentPayload = ({ purpose, channel, enabled }) => ({
  consents: [
    {
      purpose,
      channel,
      profile: selectedProfile.value,
      status: enabled ? 'ACTIVE' : 'WITHDRAWN',
      source: SOURCE,
      metadata: {
        reason:
          updateReason.value.trim() ||
          (enabled ? 'Opt-in manual no perfil' : 'Opt-out manual no perfil'),
        origin: 'lead_profile',
        channel: normalizeNotificationChannel(channel),
        purpose,
      },
    },
  ],
});

const idempotencyKeyFor = ({ purpose, channel }) =>
  [
    'lead-profile-notification-preference',
    resolvedContactId.value,
    selectedProfile.value,
    purpose,
    channel,
    Date.now(),
  ].join(':');

const updateConsent = async ({ purpose, channel, enabled }) => {
  if (!canEdit.value) {
    useAlert(preferenceError.value.message);
    return;
  }

  const key = `${purpose}:${channel}`;
  savingKey.value = key;
  try {
    const { data } =
      await NotificationsAPI.updateContactNotificationPreferences(
        resolvedContactId.value,
        consentPayload({ purpose, channel, enabled }),
        { idempotencyKey: idempotencyKeyFor({ purpose, channel }) }
      );
    snapshot.value = data || snapshot.value;
    updateReason.value = '';
    useAlert('Preferência atualizada com origem, motivo, ator e timestamp.');
  } catch (error) {
    const errorState = notificationPreferenceErrorState(error);
    preferenceError.value = errorState;
    useAlert(
      error.response?.data?.errors?.[0] ||
        errorState.message ||
        'Não foi possível atualizar a preferência.'
    );
  } finally {
    savingKey.value = null;
    emitDispatchGuard();
  }
};

watch(resolvedContactId, loadPreferences, { immediate: true });
watch(
  () => props.initialPurpose,
  value => {
    selectedPurpose.value = normalizePurposeSelection(value);
  }
);
watch([selectedPurposeChecks, preferenceLoadState], emitDispatchGuard, {
  immediate: true,
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="grid gap-4">
    <div
      class="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between"
    >
      <div class="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        <label class="grid gap-1 text-[12px] font-semibold text-lais-text">
          Perfil LGPD
          <select
            v-model="selectedProfile"
            class="h-9 rounded-lais-sm border border-[#e5e7eb] bg-white px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
          >
            <option
              v-for="profile in PROFILES"
              :key="profile.id"
              :value="profile.id"
            >
              {{ profile.label }}
            </option>
          </select>
        </label>
        <label class="grid gap-1 text-[12px] font-semibold text-lais-text">
          Finalidade do disparo
          <select
            v-model="selectedPurpose"
            class="h-9 rounded-lais-sm border border-[#e5e7eb] bg-white px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
          >
            <option
              v-for="purpose in PURPOSES"
              :key="purpose.id"
              :value="purpose.id"
            >
              {{ purpose.label }}
            </option>
          </select>
        </label>
        <label class="grid gap-1 text-[12px] font-semibold text-lais-text">
          Motivo da alteração
          <input
            v-model="updateReason"
            type="text"
            class="h-9 rounded-lais-sm border border-[#e5e7eb] bg-white px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
            placeholder="Pedido do titular, campanha, ajuste manual"
          />
        </label>
      </div>
      <button
        type="button"
        class="inline-flex h-9 items-center justify-center gap-2 rounded-lais border border-lais-border px-3 text-[12px] font-semibold text-lais-primary transition-colors hover:border-lais-primary hover:bg-[#f8f4fc] disabled:cursor-not-allowed disabled:opacity-60"
        :disabled="isLoading"
        @click="loadPreferences"
      >
        <span
          class="i-lucide-refresh-cw size-4"
          :class="{ 'animate-spin': isLoading }"
          aria-hidden="true"
        />
        Atualizar
      </button>
    </div>

    <div
      v-if="isContactBlocked"
      class="rounded-lais-sm border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] font-semibold text-amber-900"
    >
      WhatsApp bloqueado no cadastro: qualquer disparo nesse canal exige
      desbloqueio e motivo registrado.
    </div>

    <div
      v-if="incompatibleDispatches.length"
      class="rounded-lais-sm border border-[#f7c5c5] bg-[#fff5f5] px-3 py-3 text-[13px] text-[#9f1239]"
    >
      <p class="font-semibold">Alerta antes do disparo incompatível</p>
      <ul class="mt-2 grid gap-1">
        <li
          v-for="check in incompatibleDispatches"
          :key="check.id"
          class="flex items-start gap-2"
        >
          <span
            class="i-lucide-circle-alert mt-0.5 size-4 shrink-0"
            aria-hidden="true"
          />
          <span>{{ check.label }}: {{ check.message }}</span>
        </li>
      </ul>
    </div>

    <div
      v-if="isLoading"
      class="rounded-lais-sm border border-[#eee5d6] bg-[#fafafa] p-5 text-center text-[13px] font-semibold text-lais-text-muted"
    >
      Carregando preferências de notificação…
    </div>

    <div
      v-else-if="hasLoadError"
      class="rounded-lais-sm border border-dashed border-[#eee5d6] bg-[#fafafa] p-5 text-center"
    >
      <span
        class="i-lucide-shield-alert mx-auto size-8 text-[#c2410c]"
        aria-hidden="true"
      />
      <p class="mt-3 text-[14px] font-semibold text-lais-text">
        {{ preferenceError.message }}
      </p>
      <button
        v-if="preferenceLoadState !== 'forbidden'"
        type="button"
        class="mt-3 inline-flex h-9 items-center gap-2 rounded-lais border border-lais-border px-3 text-[13px] font-semibold text-lais-text transition-colors hover:border-lais-primary hover:text-lais-primary"
        @click="loadPreferences"
      >
        <span class="i-lucide-refresh-cw size-4" aria-hidden="true" />
        Tentar novamente
      </button>
    </div>

    <div v-else class="overflow-x-auto">
      <table class="w-full min-w-[720px] border-separate border-spacing-y-2">
        <thead>
          <tr class="text-left text-[11px] uppercase text-lais-text-muted">
            <th class="px-3 py-2 font-semibold">Finalidade</th>
            <th
              v-for="channel in CHANNELS"
              :key="channel.id"
              class="px-3 py-2 font-semibold"
            >
              {{ channel.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="purpose in purposeRows" :key="purpose.id">
            <th
              class="w-[220px] rounded-l border-y border-l border-[#eee5d6] bg-[#fafafa] px-3 py-3 text-left align-top"
            >
              <p class="text-[13px] font-semibold text-lais-text">
                {{ purpose.label }}
              </p>
              <p class="mt-1 text-[11px] text-lais-text-muted">
                {{ purpose.template }}
              </p>
            </th>
            <td
              v-for="channel in purpose.channels"
              :key="`${purpose.id}-${channel.id}`"
              class="border-y border-[#eee5d6] bg-white px-3 py-3 align-top last:rounded-r last:border-r"
            >
              <div class="grid min-h-[144px] gap-2">
                <div class="flex items-center justify-between gap-2">
                  <span
                    class="inline-flex items-center gap-1.5 text-[12px] font-semibold text-lais-text"
                  >
                    <span
                      class="size-4 text-lais-primary"
                      :class="channel.icon"
                      aria-hidden="true"
                    />
                    {{ channel.label }}
                  </span>
                  <span
                    class="rounded-full px-2 py-0.5 text-[11px] font-semibold"
                    :class="channel.status.className"
                  >
                    {{ channel.status.label }}
                  </span>
                </div>
                <p
                  class="text-[11px] leading-relaxed"
                  :class="
                    channel.compatibility.allowed
                      ? 'text-emerald-700'
                      : 'text-[#9f1239]'
                  "
                >
                  {{ channel.compatibility.message }}
                </p>
                <p class="text-[11px] leading-relaxed text-lais-text-muted">
                  {{ channel.audit || 'Sem ator/timestamp registrado ainda.' }}
                </p>
                <div class="mt-auto flex flex-wrap gap-2">
                  <button
                    type="button"
                    class="inline-flex h-8 items-center gap-1 rounded-lais border border-emerald-200 px-2.5 text-[11px] font-semibold text-emerald-700 transition-colors hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60"
                    :disabled="
                      !canEdit || savingKey === `${purpose.id}:${channel.id}`
                    "
                    @click="
                      updateConsent({
                        purpose: purpose.id,
                        channel: channel.id,
                        enabled: true,
                      })
                    "
                  >
                    <span class="i-lucide-check size-3.5" aria-hidden="true" />
                    Opt-in
                  </button>
                  <button
                    type="button"
                    class="inline-flex h-8 items-center gap-1 rounded-lais border border-rose-200 px-2.5 text-[11px] font-semibold text-rose-700 transition-colors hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
                    :disabled="
                      !canEdit || savingKey === `${purpose.id}:${channel.id}`
                    "
                    @click="
                      updateConsent({
                        purpose: purpose.id,
                        channel: channel.id,
                        enabled: false,
                      })
                    "
                  >
                    <span class="i-lucide-ban size-3.5" aria-hidden="true" />
                    Opt-out
                  </button>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
